import React, { useRef } from 'react';
import { Nullable } from 'global';
import moment, { Moment } from 'moment';
import { DateField } from 'components/form';
import { DateFormats } from 'constants/common';
import { useForm, useWatch } from 'react-hook-form';
import { Cancel, Event } from '@mui/icons-material';
import { Button, Stack, Typography } from '@mui/material';
import { CustomDialog } from 'components/shared/Dialog';

import { useCalendar } from '../../api';

interface Props {
  ids: string[];
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

type ReDateFormValues = {
  date: Nullable<Moment>;
};

export const ReDateEventsDialog = ({ ids, isOpen, onClose, onSuccess }: Props) => {
  const { handleReDateEventsToDate, isReDatingToDate } = useCalendar();

  const { control, handleSubmit, reset } = useForm<ReDateFormValues>({ defaultValues: { date: null } });
  const date = useWatch({ control, name: 'date' });

  // Guards against a second submit before the mutation's loading state re-renders the dialog.
  const isSubmittingRef = useRef(false);

  const isSaving = isReDatingToDate || isSubmittingRef.current;
  const canConfirm = !!date && moment(date).isValid() && !isSaving;
  const label = `${ids.length} ${ids.length === 1 ? 'event' : 'events'}`;

  const handleDialogClose = () => {
    if (isSaving) return;

    reset({ date: null });
    onClose();
  };

  const onSubmit = async (values: ReDateFormValues) => {
    if (!values.date || !moment(values.date).isValid() || isSubmittingRef.current) return;

    isSubmittingRef.current = true;

    try {
      const result = await handleReDateEventsToDate(ids, moment(values.date).format(DateFormats.IsoDate), api => ({
        awaitRefetchQueries: true,
        refetchQueries: [api.events.queries.listing, api.events.queries.list, api.events.queries.listWithActivities],
      }));

      // On failure keep the dialog open with the chosen date so the user can retry or cancel.
      if (!result) return;

      reset({ date: null });
      onSuccess();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <CustomDialog
      maxWidth="xs"
      isOpen={isOpen}
      label={`Redate ${label}`}
      onClose={handleDialogClose}
      actions={
        <Stack direction="row" gap="1rem" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Event />}
            disabled={!canConfirm}
            loading={isSaving}
            onClick={handleSubmit(onSubmit)}
          >
            Confirm
          </Button>

          <Button onClick={handleDialogClose} startIcon={<Cancel />} disabled={isSaving}>
            Cancel
          </Button>
        </Stack>
      }
    >
      <Stack gap="1rem">
        <Typography variant="body2">
          Move {label} to a new date. Each event keeps its own time of day and duration.
        </Typography>

        <DateField
          name="date"
          label="New date"
          control={control}
          dataTestId="redate-events-date"
          disabled={isSaving}
          slotProps={{ textField: { size: 'small', required: true, variant: 'outlined', autoFocus: true } }}
        />
      </Stack>
    </CustomDialog>
  );
};
